import React from "react";

/**
 * Render a money amount with the brand's signature typographic
 * detail: the currency symbol set in italic Fraunces (display),
 * the digits set in JetBrains Mono with tabular figures so columns
 * align in tables.
 *
 * Per `plans/dashboard-redesign/DESIGN_BRIEF.md` §3 and §5.2, this
 * pattern is part of the brand identity — every dollar sign in the
 * app should be set this way.
 *
 * The component splits the formatted string from `Intl.NumberFormat`
 * into its symbol and digit parts so we can style each
 * independently. The wrapper is `display: inline-flex` with
 * `align-items: baseline` so the italic `$` sits next to the
 * numbers without extra spacing weirdness.
 *
 * Props:
 *   amount      — number (cents or dollars depending on caller; the
 *                 component doesn't divide, the caller is
 *                 responsible for unit consistency).
 *   currency    — ISO currency code, default "USD".
 *   className   — extra class on the outer span (e.g. "money-lg").
 *   signed      — when true, prefix `-` for negatives and `+` for
 *                 positives. Defaults to false (only `-` shown for
 *                 negatives, no leading `+`).
 *
 * The `+`/`-` glyph is also rendered in italic Fraunces alongside
 * the symbol so the entire "money sigil" reads as a unit.
 */
const Money = ({
    amount,
    currency = "USD",
    className,
    signed = false,
    ...rest
}) => {
    const safeAmount = Number(amount) || 0;
    const isNegative = safeAmount < 0;
    const absoluteValue = Math.abs(safeAmount);

    const parts = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
    }).formatToParts(absoluteValue);

    const symbol = parts.find((p) => p.type === "currency")?.value || "$";
    const digits = parts
        .filter((p) => p.type !== "currency" && p.type !== "literal")
        .map((p) => p.value)
        .join("")
        .replace(/^\s+|\s+$/g, "");

    const prefix = isNegative ? "−" : signed ? "+" : "";

    return (
        <span className={`money-amount${className ? ` ${className}` : ""}`} {...rest}>
            <span className="money-sigil" aria-hidden="true">
                {prefix}
                {symbol}
            </span>
            <span className="money-digits">{digits}</span>
        </span>
    );
};

export default Money;
